import { type ReactNode, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Home,
  Bell,
  ClipboardList,
  ClipboardCheck,
  Archive,
  User,
  Globe,
  Sun,
  Moon,
  Monitor,
  Settings,
  CreditCard,
  LogOut,
  CircleUserRound,
  Smile,
} from 'lucide-react'
import { Transition, Popover, PopoverButton, PopoverPanel, PopoverGroup } from '@headlessui/react'
import { cn } from '@/lib/utils'
import { useTranslation, useTheme, useMediaQuery } from '@/hooks'
import { type ThemeMode } from '@/hooks/useTheme'
import { SlidingPillToggle } from '@/components/ui/filters/SlidingPillToggle'
import { DotPattern } from '@/components/ui/DotPattern'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { Logo } from '@/components/brand/Logo'
import { useI18n } from '@/hooks/useI18n'
import { type Language } from '@/i18n'
import { InboxDropdown } from '@/components/inbox'
import { TermsUpdateBanner } from './TermsUpdateBanner'

/**
 * AppLayout Component
 * Based on UX Design Guidelines §7.5 - Navigation Patterns
 *
 * Structure:
 * - Top navigation (desktop)
 * - Bottom navigation (mobile - via MobileNav)
 * - Main content area
 */

export interface AppLayoutProps {
  children: ReactNode
  /** User display name for profile menu */
  userName?: string
  /** User email for profile menu */
  userEmail?: string
  /** User avatar URL */
  userAvatar?: string
  /** Unread inbox count for bell badge */
  inboxCount?: number
  /** Handler for sign out */
  onSignOut: () => void
}

interface NavItem {
  translationKey: string
  href: string
  icon: typeof Home
}

const navItems: NavItem[] = [
  { translationKey: 'nav.dashboard', href: '/dashboard', icon: Home },
  { translationKey: 'nav.rules', href: '/rules', icon: ClipboardList },
  { translationKey: 'nav.ruleMatches', href: '/rule-matches', icon: ClipboardCheck },
  { translationKey: 'nav.archive', href: '/archive', icon: Archive },
]

// Shared profile menu content used in both desktop Popover and mobile BottomSheet
const menuRowClass =
  'group flex w-full items-center px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors'

// Explicit hover colors: transitioning children to currentColor while the row's own color
// transitions makes the icon and subtitle flicker
const menuRowTones = {
  accent: { row: 'hover:text-primary-light', child: 'group-hover:text-primary-light' },
  danger: { row: 'hover:text-red-600', child: 'group-hover:text-red-600' },
}

const themeIcons: Record<ThemeMode, typeof Home> = { light: Sun, dark: Moon, system: Monitor }

/** Icon tile + title + subtitle; icon and subtitle follow the row's hover color */
function MenuRowLabel({
  icon: Icon,
  title,
  subtitle,
  tone = 'accent',
}: {
  icon: typeof Home
  title: string
  subtitle: string
  tone?: keyof typeof menuRowTones
}) {
  return (
    <span className="flex min-w-0 items-center gap-3">
      <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500 transition-colors', menuRowTones[tone].child)}>
        <Icon className="w-4 h-4" />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[14px] font-medium">{title}</span>
        <span className={cn('block truncate text-[12px] text-gray-500 transition-colors', menuRowTones[tone].child)}>
          {subtitle}
        </span>
      </span>
    </span>
  )
}

interface ProfileMenuContentProps {
  userName?: string
  userEmail?: string
  fullHeight?: boolean
  onClose: () => void
  onSignOut: () => void
  language: Language
  setLanguage: (lang: Language) => void
  themeMode: ThemeMode
  setThemeMode: (mode: ThemeMode) => void
}

function ProfileMenuContent({
  userName,
  userEmail,
  fullHeight,
  onClose,
  onSignOut,
  language,
  setLanguage,
  themeMode,
  setThemeMode,
}: ProfileMenuContentProps) {
  const { t } = useTranslation()

  const languageOptions = [
    { value: 'bg' as const, label: 'БГ' },
    { value: 'en' as const, label: 'EN' },
  ]

  const themeOptions = [
    { value: 'light' as ThemeMode, label: '', icon: <Sun className="w-3.5 h-3.5" /> },
    { value: 'dark' as ThemeMode, label: '', icon: <Moon className="w-3.5 h-3.5" /> },
    { value: 'system' as ThemeMode, label: '', icon: <Monitor className="w-3.5 h-3.5" /> },
  ]

  return (
    <div className={cn(
      'w-full overflow-hidden',
      fullHeight ? 'bg-white' : 'w-[320px] bg-white rounded-lg shadow-lg border border-gray-100'
    )}>
      {/* Title header — hidden when embedded in BottomSheet */}
      {!fullHeight && (
        <div className="px-4 py-3 border-b border-gray-100">
          <span className="text-[15px] font-semibold text-gray-900">
            {t('nav.profile')}
          </span>
        </div>
      )}

      {/* User Info */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent ring-1 ring-inset ring-accent/20">
          <CircleUserRound className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p className="text-[14px] font-medium text-gray-900 truncate">
            {userName}
          </p>
          <p className="text-[13px] text-gray-500 truncate mt-0.5">
            {userEmail}
          </p>
        </div>
      </div>

      {/* Menu items */}
      <div>
        <Link
          to="/settings"
          onClick={onClose}
          className={cn(menuRowClass, menuRowTones.accent.row)}
        >
          <MenuRowLabel icon={Settings} title={t('nav.settings')} subtitle={t('nav.settings_hint')} />
        </Link>
        <Link
          to="/settings/billing"
          onClick={onClose}
          className={cn(menuRowClass, menuRowTones.accent.row, 'border-t border-gray-100')}
        >
          <MenuRowLabel icon={CreditCard} title={t('nav.billing')} subtitle={t('nav.billing_hint')} />
        </Link>
        {/* Language Toggle */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 text-gray-700 border-t border-gray-100">
          <MenuRowLabel
            icon={Globe}
            title={t('nav.language')}
            subtitle={language === 'bg' ? 'Български' : 'English'}
          />
          <SlidingPillToggle
            options={languageOptions}
            value={language}
            onChange={(val) => setLanguage(val as Language)}
          />
        </div>
        {/* Theme Toggle */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 text-gray-700 border-t border-gray-100">
          <MenuRowLabel
            icon={themeIcons[themeMode]}
            title={t('nav.theme')}
            subtitle={t(`nav.theme_${themeMode}`)}
          />
          <SlidingPillToggle
            options={themeOptions}
            value={themeMode}
            onChange={setThemeMode}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100">
        <button
          type="button"
          onClick={() => { onClose(); onSignOut() }}
          className={cn(menuRowClass, menuRowTones.danger.row)}
        >
          <MenuRowLabel icon={LogOut} title={t('auth.logout')} subtitle={t('nav.logout_hint')} tone="danger" />
        </button>
      </div>
    </div>
  )
}

export function AppLayout({
  children,
  userName,
  userEmail,
  userAvatar,
  inboxCount = 0,
  onSignOut,
}: AppLayoutProps) {
  const { t } = useTranslation()
  const { language, setLanguage } = useI18n()
  const { mode: themeMode, setMode: setThemeMode } = useTheme()
  const location = useLocation()
  const isDesktop = useMediaQuery('(min-width: 768px)')

  // Mobile bottom sheet state
  const [bellSheetOpen, setBellSheetOpen] = useState(false)
  const [profileSheetOpen, setProfileSheetOpen] = useState(false)

  // Auto-close sheets if viewport crosses to desktop
  useEffect(() => {
    if (isDesktop) {
      setBellSheetOpen(false)
      setProfileSheetOpen(false)
    }
  }, [isDesktop])

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  // Shared bell button visual
  const renderBellButton = (isOpen: boolean, props?: Record<string, unknown>) => (
    <div className="relative" {...props}>
      <div className={cn(
        'w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-small',
        isOpen ? 'bg-accent/10' : 'bg-gray-100'
      )}>
        <Bell className={cn(
          'w-4 h-4 transition-colors duration-small',
          isOpen ? 'text-accent' : 'text-gray-600'
        )} />
      </div>
      {inboxCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-micro font-bold text-white bg-error rounded-full">
          {inboxCount > 99 ? <Smile className="w-3 h-3" /> : inboxCount}
        </span>
      )}
    </div>
  )

  // Shared avatar button visual
  const renderAvatarButton = (isOpen: boolean) => (
    <>
      {userAvatar ? (
        <img src={userAvatar} alt="" className="w-8 h-8 rounded-full" />
      ) : (
        <div className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-small',
          isOpen ? 'bg-accent/10' : 'bg-gray-100'
        )}>
          <User className={cn(
            'w-4 h-4 transition-colors duration-small',
            isOpen ? 'text-accent' : 'text-gray-600'
          )} />
        </div>
      )}
    </>
  )

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50">
      {/* Top Navigation (Desktop) - Fixed at top */}
      <header className="flex-shrink-0 z-sticky bg-gradient-to-r from-white to-primary/[0.02] relative">
        <nav className="relative z-10 max-w-container-lg mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="text-gray-900 hover:text-accent transition-colors duration-small">
              <Logo className="h-7" />
            </Link>

            {/* Desktop Navigation - links stay unchanged */}
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon
                const active = isActive(item.href)

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    data-nav-tour={item.href.slice(1)}
                    className={cn(
                      'relative flex items-center gap-2 px-3 py-2 rounded-md',
                      'text-body-sm font-medium',
                      'transition-colors duration-small',
                      active
                        ? 'text-accent bg-accent/5'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    )}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{t(item.translationKey)}</span>
                  </Link>
                )
              })}
            </div>

            {/* Right side: Bell + Profile — desktop popovers, mobile bottom sheets */}
            <PopoverGroup className="flex items-center gap-1">
              {/* Bell icon */}
              {isDesktop ? (
                <Popover className="relative">
                  {({ open, close }) => (
                    <>
                      <PopoverButton
                        data-nav-tour="notifications"
                        className={cn(
                          'relative flex items-center justify-center p-2 rounded-full',
                          'transition-colors duration-small',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
                          open ? 'bg-accent/10' : 'hover:bg-gray-100'
                        )}
                      >
                        {renderBellButton(open)}
                      </PopoverButton>

                      <Transition
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <PopoverPanel className="absolute right-0 mt-2 z-dropdown origin-top-right">
                          <InboxDropdown onClose={close} />
                        </PopoverPanel>
                      </Transition>
                    </>
                  )}
                </Popover>
              ) : (
                <button
                  type="button"
                  data-nav-tour="notifications"
                  onClick={() => setBellSheetOpen(true)}
                  className={cn(
                    'relative flex items-center justify-center p-2 rounded-full',
                    'transition-colors duration-small',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
                    bellSheetOpen ? 'bg-accent/10' : 'hover:bg-gray-100'
                  )}
                >
                  {renderBellButton(bellSheetOpen)}
                </button>
              )}

              {/* Profile icon */}
              {isDesktop ? (
                <Popover className="relative">
                  {({ open, close }) => (
                  <>
                  <PopoverButton
                    data-nav-tour="profile"
                    className={cn(
                      'flex items-center justify-center p-2 rounded-full',
                      'transition-colors duration-small',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
                      open ? 'bg-accent/10' : 'hover:bg-gray-100'
                    )}
                  >
                    {renderAvatarButton(open)}
                  </PopoverButton>

                  <Transition
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <PopoverPanel className="absolute right-0 mt-2 z-dropdown origin-top-right">
                      <ProfileMenuContent
                        userName={userName}
                        userEmail={userEmail}
                        onClose={() => close()}
                        onSignOut={onSignOut}
                        language={language}
                        setLanguage={setLanguage}
                        themeMode={themeMode}
                        setThemeMode={setThemeMode}
                      />
                    </PopoverPanel>
                  </Transition>
                  </>
                  )}
                </Popover>
              ) : (
                <button
                  type="button"
                  data-nav-tour="profile"
                  onClick={() => setProfileSheetOpen(true)}
                  className={cn(
                    'flex items-center justify-center p-2 rounded-full',
                    'transition-colors duration-small',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
                    profileSheetOpen ? 'bg-accent/10' : 'hover:bg-gray-100'
                  )}
                >
                  {renderAvatarButton(profileSheetOpen)}
                </button>
              )}
            </PopoverGroup>
          </div>
        </nav>
        {/* Gradient bottom edge */}
        <div className="h-[2px] bg-gradient-to-r from-primary via-accent to-transparent opacity-15" />
      </header>

      {/* Mobile Bottom Sheets */}
      <BottomSheet
        isOpen={bellSheetOpen}
        onClose={() => setBellSheetOpen(false)}
        title={t('inbox.title')}
      >
        <InboxDropdown onClose={() => setBellSheetOpen(false)} fullHeight />
      </BottomSheet>

      <BottomSheet
        isOpen={profileSheetOpen}
        onClose={() => setProfileSheetOpen(false)}
        title={t('nav.profile')}
      >
        <ProfileMenuContent
          userName={userName}
          userEmail={userEmail}
          fullHeight
          onClose={() => setProfileSheetOpen(false)}
          onSignOut={onSignOut}
          language={language}
          setLanguage={setLanguage}
          themeMode={themeMode}
          setThemeMode={setThemeMode}
        />
      </BottomSheet>

      {/* Terms acceptance - stays below the top bar while the content scrolls */}
      <TermsUpdateBanner />

      {/* Main Content - Scrollable area */}
      <main id="main-content" className="relative flex-1 overflow-y-auto pb-20 md:pb-0 scrollbar-gutter-stable">
        {/* Full-page dot pattern background */}
        <div className="fixed inset-0 opacity-[0.03] pointer-events-none z-base">
          <DotPattern id="page-bg-dots" fill="currentColor" className="text-gray-400" />
        </div>
        <div className="relative">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation - shown via CSS */}
      <MobileNavInternal
        currentPath={location.pathname}
      />
    </div>
  )
}

// Mobile navigation component (internal)
interface MobileNavInternalProps {
  currentPath: string
}

function MobileNavInternal({ currentPath }: MobileNavInternalProps) {
  const { t } = useTranslation()

  const isActive = (href: string) => {
    if (href === '/') {
      return currentPath === '/'
    }
    return currentPath.startsWith(href)
  }

  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-sticky md:hidden',
        'bg-white/80 backdrop-blur-md border-t border-gray-200/50',
        'safe-area-inset-bottom'
      )}
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label={t('nav.mainNavigation')}
    >
      <div className="flex items-center h-16">
        {navItems.map((item) => {
          const Icon = item.icon
          const active = isActive(item.href)
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'relative flex flex-col items-center justify-center',
                'flex-1 h-full',
                'text-tiny font-medium whitespace-nowrap',
                'transition-colors duration-small',
                active ? 'text-accent' : 'text-gray-500'
              )}
            >
              <Icon className="w-6 h-6" />
              <span className="mt-1">{t(item.translationKey)}</span>
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
